import { useState } from 'react';
import { Button, Spin } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  LockOutlined,
  TrophyOutlined,
} from '@ant-design/icons';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import { CourseTopic, CourseTopicStatus } from './Course.const';
import useGetContractWithProgress from '../../hooks/useGetContractWithProgress';
import useGetThemeContent from '../../hooks/useGetThemeContent';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';

import css from './Course.module.css';

const deriveTopicStatus = (
  themeId: string,
  sortedThemes: CourseTopic[],
  completedThemeIds: Set<string>
): CourseTopicStatus => {
  if (completedThemeIds.has(themeId)) return 'completed';
  const firstIncomplete = sortedThemes.find(t => !completedThemeIds.has(t.id));
  if (firstIncomplete?.id === themeId) return 'current';
  return 'locked';
};

const Course = () => {
  const history = useHistory();
  const { id: contractId } = useParams<{ id: string }>();

  const {
    contract,
    themes,
    listenerUid,
    completedThemeIds,
    markThemeCompleted,
    loading,
  } = useGetContractWithProgress({ contractId });

  const sortedThemes = [...themes].sort((a, b) => a.order - b.order);
  const topics: CourseTopic[] = sortedThemes.map(t => ({
    ...t,
    status: deriveTopicStatus(t.id, sortedThemes, completedThemeIds),
  }));

  const firstUnlocked = topics.find(t => t.status !== 'locked') ?? topics[0];
  const [activeTopicId, setActiveTopicId] = useState<string | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const resolvedActiveId = activeTopicId ?? firstUnlocked?.id ?? null;

  const { body, loading: contentLoading } = useGetThemeContent({
    contractId,
    themeId: resolvedActiveId ?? '',
  });

  const completedCount = topics.filter(t => completedThemeIds.has(t.id)).length;
  const total = topics.length;
  const progress = total > 0 ? (completedCount / total) * 100 : 0;

  const activeIndex = topics.findIndex(t => t.id === resolvedActiveId);
  const activeTopic = topics[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === total - 1;

  const goPrev = () => {
    if (!isFirst) setActiveTopicId(topics[activeIndex - 1].id);
  };

  const goNext = async () => {
    if (!activeTopic || !listenerUid) return;
    try {
      await api.post(
        `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${listenerUid}/themes/${activeTopic.id}/complete`,
        {}
      );
      markThemeCompleted(activeTopic.id);
      if (isLast) {
        setShowCompletion(true);
      } else {
        setActiveTopicId(topics[activeIndex + 1].id);
      }
    } catch (err: any) {
      const status = err?.statusCode ?? err?.status;
      if (status === 422) {
        toast.showOnFailure('Необходимо пройти предыдущие темы последовательно');
      } else if (status === 402) {
        toast.showOnFailure('Курс недоступен — договор не оплачен');
      } else {
        toast.showOnFailure('Ошибка подтверждения темы');
      }
    }
  };

  if (loading) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <Spin />
        </div>
      </PageWrapper>
    );
  }

  if (!topics.length) {
    return (
      <PageWrapper hideLeftPanel>
        <div className={css.Layout}>
          <aside className={css.Sidebar}>
            <button
              type='button'
              className={css.BackBtn}
              onClick={() => history.push(APP_URL.MY_COURSES)}
            >
              <ArrowLeftOutlined /> К моим курсам
            </button>
            <div className={css.SidebarHeader}>
              <h2 className={css.SidebarTitle}>Содержание курса</h2>
            </div>
            <p style={{ padding: '16px' }}>Содержание курса не добавлено</p>
          </aside>
          <section className={css.Main}>
            <header className={css.MainHeader}>
              <div className={css.MainHeaderTop}>
                <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              </div>
            </header>
          </section>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper hideLeftPanel>
      <div className={css.Layout}>
        <aside className={css.Sidebar}>
          <button
            type='button'
            className={css.BackBtn}
            onClick={() => history.push(APP_URL.MY_COURSES)}
          >
            <ArrowLeftOutlined /> К моим курсам
          </button>
          <div className={css.SidebarHeader}>
            <h2 className={css.SidebarTitle}>Содержание курса</h2>
            <p className={css.SidebarProgressText}>
              {completedCount} из {total} тем пройдено
            </p>
            <div className={css.ProgressTrack}>
              <div
                className={css.ProgressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <ul className={css.TopicList}>
            {topics.map((topic) => {
              const isActive = topic.id === resolvedActiveId;
              return (
                <li
                  key={topic.id}
                  className={`${css.TopicItem} ${isActive ? css.TopicItemActive : ''}`}
                  onClick={() => {
                    if (topic.status !== 'locked') setActiveTopicId(topic.id);
                  }}
                >
                  <span
                    className={`${css.TopicNumber} ${isActive ? css.TopicNumberActive : ''}`}
                  >
                    {topic.order}
                  </span>
                  <div className={css.TopicInfo}>
                    <div className={css.TopicTitle}>{topic.title}</div>
                    <div className={css.TopicMeta}>—</div>
                  </div>
                  <span className={css.TopicStatus}>
                    {topic.status === 'completed' && (
                      <CheckCircleFilled className={css.IconCompleted} />
                    )}
                    {topic.status === 'locked' && (
                      <LockOutlined className={css.IconLocked} />
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className={css.Main}>
          <header className={css.MainHeader}>
            <div className={css.MainHeaderTop}>
              <h1 className={css.CourseTitle}>{contract?.course?.name ?? ''}</h1>
              <span className={css.StatusBadge}>
                <ClockCircleOutlined /> {contract?.status ?? ''}
              </span>
            </div>
          </header>

          {showCompletion && (
            <div className={css.CompletionScreen}>
              <TrophyOutlined className={css.CompletionIcon} />
              <h2 className={css.CompletionTitle}>Вы успешно закончили обучение!</h2>
              <p className={css.CompletionText}>
                Вам будет выдан сертификат в ближайшее время.
              </p>
              <Button
                type='primary'
                className={css.CompletionBtn}
                onClick={() => history.push(APP_URL.MY_COURSES)}
              >
                К моим курсам
              </Button>
            </div>
          )}

          {!showCompletion && activeTopic && (
            <>
              <div className={css.TopicHeader}>
                <div className={css.TopicHeaderMeta}>
                  Тема {activeTopic.order} из {total}
                </div>
                <h2 className={css.TopicHeaderTitle}>{activeTopic.title}</h2>
              </div>

              <div className={css.Content}>
                {contentLoading ? (
                  <Spin />
                ) : (
                  (body ?? '').split('\n\n').map((seg, idx) => (
                    <p key={idx} className={css.ContentParagraph}>{seg}</p>
                  ))
                )}
              </div>

              <div className={css.Footer}>
                <Button
                  className={css.NavBtn}
                  icon={<ArrowLeftOutlined />}
                  disabled={isFirst}
                  onClick={goPrev}
                >
                  Назад
                </Button>
                <div className={css.FooterRight}>
                  <Button
                    type='primary'
                    className={css.NextBtn}
                    onClick={goNext}
                    disabled={!listenerUid}
                  >
                    {isLast ? 'Завершить курс' : 'Следующая'}
                    {!isLast && <ArrowRightOutlined />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </PageWrapper>
  );
};

export default Course;
